/**
 * ThreadReaderToolbar — the thread reader's action bar (plan §12).
 *
 *   ←   ▤ 🗑 ✉ ⏱ │ 🗀 🏷 │ 👥        1 of 248  ‹ ›  🖨 ↗
 *
 * Three groups behind dividers, then a right cluster (position, prev/next,
 * print, open-in-provider).
 *
 * ## Icon-only, all of it
 *
 * Move to, Tag and Assign used to carry text labels while their four
 * neighbours did not, which gave the bar two button heights and a ragged
 * rhythm the design does not have. Every control here is a 32×32 square with a
 * `title`; the label survives as the accessible name, so nothing is lost to a
 * screen reader — only to the eye, which is the point.
 *
 * ## Presentational on purpose
 *
 * This component owns menu open/close state and nothing else. Every action is a
 * prop, so the mutation hooks (`useMailState`) stay in the reader that knows
 * which rows the toolbar is acting on. A toolbar that fired its own mutations
 * would need to know the lane, the target shape and the privilege boundary —
 * three things `useMailSelection`/`useMailState` already resolve once.
 *
 * ## Absent vs disabled — the rule this phase turns on
 *
 * `openInProvider(origin)` returning null means **render nothing**: no control,
 * not a disabled one and certainly not a link that 404s. That is what #596 was
 * actually asking for — the capability was fine, the old link builder was not
 * (it was hardcoded to `outlook.office.com` for every message, so Gmail mail in
 * a pooled inbox got a dead link under a label naming the wrong product).
 *
 * Everything else is the opposite: a control whose capability does not exist in
 * this repo stays VISIBLE and DISABLED, with a title saying why. Hiding it
 * would make the reader look finished when it is not. Two controls have been
 * in that state:
 *   - **Tag** — live since #646 (org tags on mail, `POST /api/mail/tags`), and
 *     disabled only where the lane may not tag: the parent passes no
 *     `onChangeConversationTags` to a unified inbox's viewer.
 *   - **Mark unread** — `POST /inbox/personal/messages/read` only ever sets
 *     `is_read: True`; no endpoint clears it. The parent therefore passes no
 *     `onMarkUnread`, and the button disables itself rather than lying.
 *     (The backend for it — `POST /api/mail/read` with `read: false` —
 *     now exists; the personal reader passes the handler.)
 *
 * A control is disabled whenever its handler prop is missing, so "no backend"
 * and "not wired on this surface" render identically and honestly.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    ArrowLeft,
    ChevronLeft,
    ChevronRight,
    Clock,
    ExternalLink,
    FolderOpen,
    MailOpen,
    Printer,
    Tag,
    Trash2,
    UserPlus,
} from 'lucide-react';

import { actionDef } from './mailRowState';
import { mailStateScopeHint, openInProvider } from './originIdentity';
import MailTagPicker from './MailTagPicker';

/**
 * Snooze presets, in the order the prototype lists them.
 *
 * The labels are the menu; the instants come from
 * `resolveSnoozePreset(key, now)` in `hooks/useMailState.js`, which resolves
 * them in the VIEWER's zone. This module deliberately does not resolve them —
 * doing so here would put a second wall-clock interpretation in the tree, which
 * is the #578/#540 defect class.
 */
export const SNOOZE_PRESETS = [
    { key: 'later_today', label: 'Later today' },
    { key: 'tomorrow', label: 'Tomorrow' },
    { key: 'this_weekend', label: 'This weekend' },
    { key: 'next_week', label: 'Next week' },
];

/**
 * The only folders `POST /api/mail/move` can actually place a message in.
 *
 * `services/mail_folders.py` counts exactly these, and `set_folder` writes the
 * raw string to `mail_folder` — so offering anything else would write a folder
 * no list query reads and the message would vanish. `inbox` is the null case:
 * the backend models "in the inbox" as `mail_folder: null`, not as the literal
 * string.
 *
 * Trash is the exception at the call site: the menu sends it to `onDelete`,
 * never `onMove('trash')`. Moving to Trash IS Delete, and on a shared inbox
 * the two are not even the same request — that lane's Trash is a per-inbox
 * removal record, and `/mail/move` refuses `trash` there.
 */
export const MOVE_TARGETS = [
    { id: 'inbox', label: 'Inbox', folder: null },
    { id: 'spam', label: 'Spam', folder: 'spam' },
    { id: 'trash', label: 'Trash', folder: 'trash' },
];

/**
 * The move targets this lane's rail actually reported, in MOVE_TARGETS order.
 *
 * Reads the same model `useMailFolders` hands the rail rather than a second
 * hardcoded list, so a lane whose backend stops counting Spam stops offering a
 * move into it. Returns `[]` for an empty/absent model, which disables the
 * control — never a menu of folders that may not exist.
 */
export function movableFolders(model) {
    const ids = new Set();
    for (const item of model?.system || []) ids.add(item.id);
    for (const group of model?.groups || []) {
        for (const child of group.children || []) ids.add(child.id);
    }
    return MOVE_TARGETS.filter(target => ids.has(target.id));
}

/** A toolbar control. No handler ⇒ disabled, never hidden. */
function ToolbarButton({ icon: Icon, label, title, onClick, disabled, testId, ...rest }) {
    const isDisabled = disabled || typeof onClick !== 'function';
    return (
        <button
            type="button"
            title={title || label}
            aria-label={label}
            disabled={isDisabled}
            data-testid={testId}
            onClick={isDisabled ? undefined : onClick}
            className={`w-8 h-8 inline-flex items-center justify-center rounded-[8px]
                        flex-shrink-0
                        ${isDisabled
                            ? 'text-[#D6D3CD] cursor-not-allowed'
                            : 'text-[#57504B] hover:bg-[#F4F2EE]'}`}
            {...rest}
        >
            {Icon ? <Icon className="w-[17px] h-[17px] flex-shrink-0" /> : null}
        </button>
    );
}

/** A divider between toolbar groups. */
function Divider() {
    return <span aria-hidden="true" className="w-px h-[18px] bg-[#E9E7E2] mx-[7px] flex-shrink-0" />;
}

/**
 * A menu anchored to its trigger.
 *
 * Hand-rolled rather than Radix: the reader's toolbar is one of the few places
 * a portal actively hurts — RuntimeShell's fullscreen contract puts the reader
 * in a bounded column, and a portalled menu escapes the scroll container it is
 * anchored in.
 */
function ToolbarMenu({ trigger, children, open, onOpenChange, testId }) {
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = event => {
            if (ref.current && !ref.current.contains(event.target)) onOpenChange(false);
        };
        const onKey = event => {
            if (event.key === 'Escape') onOpenChange(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onOpenChange]);

    return (
        <span className="relative inline-flex" ref={ref}>
            {trigger}
            {open && (
                <div
                    role="menu"
                    data-testid={testId}
                    className="absolute top-[36px] left-0 z-[34] min-w-[200px] p-[6px]
                               rounded-[11px] bg-white border border-[#E9E7E2]
                               shadow-[0_14px_36px_rgba(12,10,9,.16)]"
                >
                    {children}
                </div>
            )}
        </span>
    );
}

/** A row inside a toolbar menu. */
function MenuItem({ label, hint, onClick, disabled, testId }) {
    const isDisabled = disabled || typeof onClick !== 'function';
    return (
        <button
            type="button"
            role="menuitem"
            disabled={isDisabled}
            title={hint || undefined}
            data-testid={testId}
            onClick={isDisabled ? undefined : onClick}
            className={`w-full text-left px-[10px] py-[7px] rounded-[7px] text-[13px]
                        ${isDisabled
                            ? 'text-[#C4BFB9] cursor-not-allowed'
                            : 'text-[#26221F] hover:bg-[#F4F2EE]'}`}
        >
            {label}
        </button>
    );
}

export default function ThreadReaderToolbar({
    /** The surface contract — `supportsAssign` is the only field read. */
    surface,
    /**
     * The origin of the message the reader is anchored on. Drives the
     * open-in-provider control and the scope hints; never re-derived here.
     */
    origin,
    onBack,
    onArchive,
    /**
     * Whether the CONVERSATION is archived (`conversationArchived` in
     * `mailRowState.js`, #721). Decides the verb: Archive, or Move to Inbox on
     * a conversation opened from Archived — where a static Archive was a no-op
     * that still said it had worked.
     */
    archived = false,
    /**
     * Whether any message of the conversation is in Trash or Spam
     * (`conversationBinned`). With `archived`, decides whether the Move-to menu
     * offers Inbox: on an archived conversation outside the bin it would clear
     * nothing, so it is withheld in favour of the toolbar's own Move to Inbox.
     */
    binned = false,
    onDelete,
    onMarkUnread,
    /**
     * `(presetKey: string) => void`. The KEY, not an instant: resolving
     * "tomorrow 09:00" to a wall clock is `resolveSnoozePreset`'s job, and a
     * second interpretation of the same phrase in this tree is the #578/#540
     * defect class.
     */
    onSnooze,
    /** `(folder: string|null) => void` — null returns the thread to the Inbox. */
    onMove,
    /** The lane's folder model, as `useMailFolders` returns it. */
    folderModel,
    onAssign,
    /** 1-based position of this thread in the list it was opened from. */
    position,
    total,
    onPrev,
    onNext,
    onPrint,
    /** The conversation's tags (union over its messages), `[{ tag_id, name, color }]`. */
    conversationTags,
    /**
     * `({ add, remove }) => Promise` — tag_ids for the WHOLE conversation (#646).
     * Absent ⇒ the Tag control renders disabled (a unified inbox's viewer).
     */
    onChangeConversationTags,
}) {
    const [snoozeOpen, setSnoozeOpen] = useState(false);
    const [tagOpen, setTagOpen] = useState(false);
    const closeTag = useCallback(() => setTagOpen(false), []);
    const tagAnchorRef = useRef(null);
    const [moveOpen, setMoveOpen] = useState(false);
    // Move to → Inbox clears only `mail_folder`. On an archived conversation that
    // is not in a bin there is nothing for it to clear — it reported success and
    // left the conversation in Archived (#721) — so the menu does not offer it;
    // the Move to Inbox control beside it is the one that un-archives.
    const moveTargets = movableFolders(folderModel).filter(
        (target) => !(target.id === 'inbox' && archived && !binned),
    );
    const provider = openInProvider(origin);

    const closeSnooze = useCallback(() => setSnoozeOpen(false), []);
    const closeMove = useCallback(() => setMoveOpen(false), []);

    const snoozeHint = mailStateScopeHint(origin, 'Snooze');
    // The label and icon come from the row strip's own definition, so the
    // reader and the row cannot word the same verb two ways.
    const archiveDef = actionDef('archive', { archived_at: archived ? true : null });
    const archiveHint = mailStateScopeHint(origin, archiveDef.title);

    return (
        <div
            /* Sticky, as §12 draws it. The thread scrolls under this bar, and
               a static toolbar meant Back, Archive, Snooze and Move-to all
               scrolled away with the first screenful — on a long thread they
               were unreachable without scrolling the whole conversation back
               up. The footer is sticky for the same reason. */
            className="flex-shrink-0 flex items-center gap-1 px-5 py-2
                       border-b border-[#EDEBE6] bg-white"
            data-testid="thread-toolbar"
        >
            <span className="mr-[6px] flex-shrink-0">
                <ToolbarButton
                    icon={ArrowLeft}
                    label="Back"
                    onClick={onBack}
                    testId="thread-toolbar-back"
                />
            </span>

            {/* Group 1 — state that moves the message out of the reader. */}
            <ToolbarButton
                icon={archiveDef.icon}
                label={archiveDef.title}
                title={archiveHint || archiveDef.title}
                onClick={onArchive}
                testId="thread-toolbar-archive"
            />
            <ToolbarButton
                icon={Trash2}
                label="Delete"
                onClick={onDelete}
                testId="thread-toolbar-delete"
            />
            <ToolbarButton
                icon={MailOpen}
                label="Mark unread"
                title={
                    typeof onMarkUnread === 'function'
                        ? 'Mark unread'
                        : 'Mark unread is unavailable on this surface'
                }
                onClick={onMarkUnread}
                testId="thread-toolbar-mark-unread"
            />
            <ToolbarMenu
                open={snoozeOpen}
                onOpenChange={setSnoozeOpen}
                testId="thread-toolbar-snooze-menu"
                trigger={
                    <ToolbarButton
                        icon={Clock}
                        label="Snooze"
                        title={snoozeHint || 'Snooze'}
                        onClick={onSnooze ? () => setSnoozeOpen(v => !v) : undefined}
                        testId="thread-toolbar-snooze"
                        aria-haspopup="menu"
                        aria-expanded={snoozeOpen}
                    />
                }
            >
                {SNOOZE_PRESETS.map(preset => (
                    <MenuItem
                        key={preset.key}
                        label={preset.label}
                        testId={`thread-toolbar-snooze-${preset.key}`}
                        onClick={() => {
                            closeSnooze();
                            onSnooze(preset.key);
                        }}
                    />
                ))}
            </ToolbarMenu>

            <Divider />

            {/* Group 2 — placement. Custom folders and labels do not exist. */}
            <ToolbarMenu
                open={moveOpen}
                onOpenChange={setMoveOpen}
                testId="thread-toolbar-move-menu"
                trigger={
                    <ToolbarButton
                        icon={FolderOpen}
                        label="Move to"
                        onClick={
                            onMove && moveTargets.length
                                ? () => setMoveOpen(v => !v)
                                : undefined
                        }
                        testId="thread-toolbar-move"
                        aria-haspopup="menu"
                        aria-expanded={moveOpen}
                    />
                }
            >
                {moveTargets.map(target => {
                    // Trash is the Delete button by another route, so it
                    // takes Delete's handler and Delete's gate: disabled
                    // wherever Delete is (a unified inbox below manager).
                    const act = target.folder === 'trash'
                        ? onDelete
                        : () => onMove(target.folder);
                    return (
                        <MenuItem
                            key={target.id}
                            label={target.label}
                            testId={`thread-toolbar-move-${target.id}`}
                            onClick={typeof act === 'function'
                                ? () => {
                                    closeMove();
                                    act();
                                }
                                : undefined}
                        />
                    );
                })}
                <div className="my-1 h-px bg-stone-100" aria-hidden="true" />
                <MenuItem
                    label="Custom folders…"
                    hint="Custom folders aren't available yet — the backend returns system folders only"
                    disabled
                    testId="thread-toolbar-move-custom"
                />
            </ToolbarMenu>
            {/* #646 — tags the whole CONVERSATION; a single message is tagged
                from its own ⋯ menu. Disabled (not hidden) where the lane may
                not tag, like every other control here. */}
            <span className="relative inline-flex" ref={tagAnchorRef}>
                <ToolbarButton
                    icon={Tag}
                    label="Tag"
                    title={
                        typeof onChangeConversationTags === 'function'
                            ? 'Tag conversation'
                            : "You can't tag mail in this inbox"
                    }
                    onClick={
                        typeof onChangeConversationTags === 'function'
                            ? () => setTagOpen(v => !v)
                            : undefined
                    }
                    testId="thread-toolbar-tag"
                    aria-haspopup="dialog"
                    aria-expanded={tagOpen}
                />
                {tagOpen && typeof onChangeConversationTags === 'function' && (
                    <div className="absolute top-[36px] left-0 z-[34]">
                        <MailTagPicker
                            tags={conversationTags || []}
                            heading="Tag conversation"
                            testId="thread-toolbar-tag-picker"
                            anchorRef={tagAnchorRef}
                            onClose={closeTag}
                            onChange={onChangeConversationTags}
                        />
                    </div>
                )}
            </span>

            {/* Group 3 — assignment. A personal inbox has exactly one reader,
                and `POST /api/mail/assign` answers 422 `assign_requires_inbox`
                when it is called without one, so the control is absent rather
                than a button that always errors. */}
            {surface?.supportsAssign && (
                <>
                    <Divider />
                    <ToolbarButton
                        icon={UserPlus}
                        label="Assign"
                        onClick={onAssign}
                        testId="thread-toolbar-assign"
                    />
                </>
            )}

            {/* Right cluster. */}
            <div className="ml-auto flex items-center gap-1">
                {Number.isFinite(total) && Number.isFinite(position) && (
                    <span
                        className="text-[12.5px] text-[#8A837E] tabular-nums px-1 whitespace-nowrap"
                        data-testid="thread-toolbar-position"
                    >
                        {position} of {total}
                    </span>
                )}
                <ToolbarButton
                    icon={ChevronLeft}
                    label="Previous message"
                    onClick={onPrev}
                    testId="thread-toolbar-prev"
                />
                <ToolbarButton
                    icon={ChevronRight}
                    label="Next message"
                    onClick={onNext}
                    testId="thread-toolbar-next"
                />
                <ToolbarButton
                    icon={Printer}
                    label="Print"
                    onClick={onPrint}
                    testId="thread-toolbar-print"
                />
                {/* Absent, never dead: no provider copy ⇒ no control at all.
                    The href and the label both come from the server-resolved
                    origin, so a Gmail message can never render an
                    outlook.office.com URL (#596). */}
                {provider && (
                    <a
                        href={provider.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={provider.label}
                        aria-label={provider.label}
                        data-testid="thread-toolbar-open-in-provider"
                        className="w-8 h-8 inline-flex items-center justify-center flex-shrink-0
                                   rounded-[8px] text-[#57504B] hover:bg-[#F4F2EE]"
                    >
                        <ExternalLink className="w-4 h-4" />
                    </a>
                )}
            </div>
        </div>
    );
}
